import React, { useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { memberApi } from "../api/member";
import LanguageSupportNotice from "../Components/LanguageSupportNotice";
import { siteMeta } from "../siteData";
import { pageRoutes } from "../utils";

const servingOptions = [
  "Mezmur / Choir Service (ዝማሬ)",
  "Sunday School Teachers (ትምህርተ ሃይማኖት)",
  "Charity and Service Ministry (ማህበረ ሰብ አገልግሎት)",
  "Other",
];
const stewardshipSuggestedAmounts = [25, 50, 100, 250, 500, 1000];
const householdSizeOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10+"];
const relationshipOptions = [
  ["", "Relationship optional"],
  ["spouse", "Spouse"],
  ["child", "Child"],
  ["parent", "Parent"],
  ["sibling", "Sibling"],
  ["relative", "Relative"],
  ["other", "Other"],
];
const ageGroupOptions = [
  ["", "Age group optional"],
  ["adult", "Adult"],
  ["teen", "Teen"],
  ["child", "Child"],
  ["infant", "Infant"],
  ["prefer_not_to_say", "Prefer not to say"],
];
const membershipPaymentModeOptions = [
  {
    id: "no_immediate_payment",
    title: "Register now, no immediate payment",
    description: "The parish will review your registration and follow up.",
  },
  {
    id: "choose_contribution",
    title: "Choose membership contribution",
    description: "Select a membership contribution before secure Stripe Checkout.",
  },
];
const contributionOptions = [
  {
    id: "individual_membership",
    title: "Individual membership contribution",
    description: "Continue to secure Stripe Checkout after registration.",
  },
  {
    id: "household_membership",
    title: "Household membership contribution",
    description: "Continue to secure Stripe Checkout after registration.",
  },
];

const initialForm = {
  fullName: "",
  password: "",
  confirmPassword: "",
  householdName: "",
  email: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "Madison",
  stateRegion: "WI",
  postalCode: "",
  householdSize: "1",
  householdMembers: [],
  membershipInterest: "member",
  membershipPaymentMode: "no_immediate_payment",
  contributionType: "individual_membership",
  stewardshipPreference: "not_specified",
  stewardshipAmount: "",
  communicationPreference: "email",
  canReceiveUpdates: true,
  servingInterests: [],
  otherServingInterest: "",
  notes: "",
};

function createBlankHouseholdMember() {
  return {
    firstName: "",
    lastName: "",
    relationship: "",
    ageGroup: "",
    email: "",
    phone: "",
  };
}

function getAdditionalMemberCount(householdSize, householdMembers = []) {
  if (householdSize === "10+") {
    return Math.max(9, householdMembers.length);
  }
  return Math.max(Number(householdSize) - 1, 0);
}

function normalizeHouseholdMembers(householdSize, householdMembers) {
  const targetCount = getAdditionalMemberCount(householdSize, householdMembers);
  const nextMembers = (householdMembers || []).slice(0, targetCount);
  while (nextMembers.length < targetCount) {
    nextMembers.push(createBlankHouseholdMember());
  }
  return nextMembers;
}

function cleanHouseholdMembers(householdMembers) {
  return householdMembers
    .map((member) => {
      const cleaned = {};
      Object.entries(member).forEach(([key, value]) => {
        const trimmed = String(value || "").trim();
        if (trimmed) {
          cleaned[key] = trimmed;
        }
      });
      return cleaned;
    })
    .filter((member) => Object.keys(member).length > 0);
}

function validateOptionalHouseholdFields(householdMembers) {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  for (const member of householdMembers) {
    if (member.email && !emailPattern.test(member.email)) {
      throw new Error("Household member email address is invalid.");
    }
    if (member.phone) {
      const digitCount = member.phone.replace(/\D/g, "").length;
      if (digitCount < 7 || digitCount > 15) {
        throw new Error("Household member phone number is invalid.");
      }
    }
  }
}

function splitFullName(fullName) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || parts[0] || "",
  };
}

export default function MemberPortal() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [registrationNotice, setRegistrationNotice] = useState(null);

  function updateField(field, value) {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateMembershipPaymentMode(value) {
    setFormData((current) => ({
      ...current,
      membershipPaymentMode: value,
      contributionType:
        value === "choose_contribution"
          ? current.contributionType || "individual_membership"
          : current.contributionType,
    }));
  }

  function updateHouseholdSize(value) {
    setFormData((current) => ({
      ...current,
      householdSize: value,
      householdMembers: normalizeHouseholdMembers(value, current.householdMembers),
    }));
  }

  function updateHouseholdMember(index, field, value) {
    setFormData((current) => {
      const householdMembers = normalizeHouseholdMembers(
        current.householdSize,
        current.householdMembers
      );
      householdMembers[index] = {
        ...householdMembers[index],
        [field]: value,
      };
      return {
        ...current,
        householdMembers,
      };
    });
  }

  function addHouseholdMember() {
    setFormData((current) => ({
      ...current,
      householdSize: "10+",
      householdMembers: [
        ...normalizeHouseholdMembers("10+", current.householdMembers),
        createBlankHouseholdMember(),
      ],
    }));
  }

  function handleStewardshipPreferenceChange(value) {
    setFormData((current) => ({
      ...current,
      stewardshipPreference: value,
      stewardshipAmount:
        value === "not_specified" ? "" : current.stewardshipAmount || "25",
    }));
  }

  function toggleServingInterest(value) {
    setFormData((current) => {
      const exists = current.servingInterests.includes(value);
      const nextServingInterests = exists
        ? current.servingInterests.filter((item) => item !== value)
        : [...current.servingInterests, value];

      return {
        ...current,
        servingInterests: nextServingInterests,
        otherServingInterest:
          value === "Other" && exists ? "" : current.otherServingInterest,
      };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (status === "loading") {
      return;
    }

    setStatus("loading");
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const { firstName, lastName } = splitFullName(formData.fullName);
      const membershipType =
        formData.membershipPaymentMode === "choose_contribution"
          ? formData.contributionType
          : "no_payment";
      const householdMembers = cleanHouseholdMembers(
        normalizeHouseholdMembers(formData.householdSize, formData.householdMembers)
      );
      validateOptionalHouseholdFields(householdMembers);
      if (
        formData.stewardshipPreference !== "not_specified" &&
        (!Number(formData.stewardshipAmount) ||
          Number(formData.stewardshipAmount) <= 0)
      ) {
        throw new Error("Enter a giving amount before continuing to secure checkout.");
      }
      const payload = await memberApi("/api/auth/register-member", {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          firstName,
          lastName,
          membershipType,
          membershipPaymentMode: formData.membershipPaymentMode,
          contributionType:
            formData.membershipPaymentMode === "choose_contribution"
              ? formData.contributionType
              : "",
          familyName: formData.householdName,
          preferredLanguage: "en",
          confirmPassword: formData.confirmPassword,
          ministryInterests: formData.servingInterests.includes("Other")
            ? [
                ...formData.servingInterests.filter((item) => item !== "Other"),
                formData.otherServingInterest.trim()
                  ? `Other: ${formData.otherServingInterest.trim()}`
                  : "Other",
              ]
            : formData.servingInterests,
          householdSize:
            formData.householdSize === "10+"
              ? "10+"
              : Number(formData.householdSize),
          householdMembers,
          stewardshipAmount: formData.stewardshipAmount
            ? Number(formData.stewardshipAmount)
            : 0,
          servingInterests: formData.servingInterests.includes("Other")
            ? [
                ...formData.servingInterests.filter((item) => item !== "Other"),
                formData.otherServingInterest.trim()
                  ? `Other: ${formData.otherServingInterest.trim()}`
                  : "Other",
              ]
            : formData.servingInterests,
        }),
      });

      if (payload.status === "payment_required" && payload.checkoutUrl) {
        window.location.assign(payload.checkoutUrl);
        return;
      }

      if (payload.status === "verification_required") {
        const verificationEmail = payload.email || formData.email;
        sessionStorage.setItem("pendingVerificationEmail", verificationEmail);
        if (payload.verificationToken) {
          sessionStorage.setItem(
            "pendingVerificationToken",
            payload.verificationToken
          );
        }
        sessionStorage.setItem(
          "pendingVerificationMessage",
          "Registration received. Please enter the 4-digit code sent to your email to finish verification."
        );
        setStatus("success");
        setSuccessMessage("");
        setRegistrationNotice({
          email: verificationEmail,
          message:
            formData.stewardshipPreference !== "not_specified"
              ? "Your membership registration was received. We sent a 4-digit verification code to your email. After verification, you will continue to Stripe-hosted Checkout to enter contact and payment information securely."
              : "Your membership registration was received. We sent a 4-digit verification code to your email. Please enter the code to finish your registration.",
        });
        return;
      }

      setStatus("success");
      setSuccessMessage(
        payload.message ||
          "Your member account has been created. Redirecting to your dashboard."
      );
      setFormData(initialForm);
      navigate(pageRoutes.MemberLogin);
    } catch (error) {
      setStatus("error");
      setRegistrationNotice(null);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to submit the membership form."
      );
    }
  }

  function continueToVerification() {
    navigate(pageRoutes.MemberVerifyEmail);
  }

  return (
    <div className="font-sans-clean min-h-screen bg-[#FDF8F3]">
      {registrationNotice && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#191516]/55 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="registration-received-title"
        >
          <div className="w-full max-w-md rounded-3xl border border-emerald-200 bg-white p-7 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#A88520]">
                  Registration Received
                </p>
                <h2
                  id="registration-received-title"
                  className="mt-2 font-serif-elegant text-2xl font-bold text-[#722F37]"
                >
                  Please verify your email
                </h2>
              </div>
            </div>

            <p className="mt-5 text-sm leading-6 text-gray-700">
              {registrationNotice.message}
            </p>
            <p className="mt-3 rounded-2xl bg-[#FDF8F3] px-4 py-3 text-sm font-semibold text-gray-800">
              Code sent to: {registrationNotice.email}
            </p>
            <p className="mt-3 text-xs leading-5 text-gray-500">
              If you do not see the email within a few minutes, check your spam
              or junk folder, then use Resend code on the verification page.
            </p>

            <button
              type="button"
              onClick={continueToVerification}
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C]"
            >
              Enter verification code
            </button>
          </div>
        </div>
      )}

      <section className="relative py-24 sm:py-32 overflow-hidden bg-[#722F37]">
        <div className="absolute inset-0 opacity-20 pointer-events-none ethiopian-cross" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-sm font-semibold tracking-[0.24em] uppercase text-[#E7D6A8] mb-3">
            Member Portal
          </p>
          <h1 className="font-serif-elegant text-4xl sm:text-5xl font-bold text-white mb-4">
            Register your household and stay connected to parish life.
          </h1>
          <p className="text-lg text-white/80 max-w-3xl">
            Create your member account, register your household, and connect your
            membership record to secure Stripe Checkout when a membership contribution
            is selected.
          </p>
          <Link
            to={pageRoutes.MemberLogin}
            className="mt-6 inline-flex rounded-xl border border-white/40 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
          >
            Already registered? Sign in
          </Link>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <LanguageSupportNotice />
        <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-8 items-start">
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl shadow-sm border border-[#C9A227]/10 p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-6 h-6 text-[#722F37]" />
              <div>
                <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37]">
                  Parish Membership Form
                </h2>
                <p className="text-sm text-gray-600">
                  Use this form to register as a member, request updates, or submit a
                  household record update.
                </p>
              </div>
            </div>

            {successMessage && (
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-900">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{successMessage}</p>
                </div>
              </div>
            )}

            {errorMessage && (
              <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-900">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name (ሙሉ ስም)
                </span>
                <input
                  value={formData.fullName}
                  onChange={(event) => updateField("fullName", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  required
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </span>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(event) => updateField("password", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  autoComplete="new-password"
                  required
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm password
                </span>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(event) =>
                    updateField("confirmPassword", event.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  autoComplete="new-password"
                  required
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Baptism Name (የክርስትና ስም)
                </span>
                <input
                  value={formData.householdName}
                  onChange={(event) => updateField("householdName", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Email</span>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField("email", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  required
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">Phone</span>
                <input
                  value={formData.phone}
                  onChange={(event) => updateField("phone", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  required
                />
              </label>

              <label className="block sm:col-span-2">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Address line 1
                </span>
                <input
                  value={formData.addressLine1}
                  onChange={(event) => updateField("addressLine1", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block sm:col-span-2">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Address line 2
                </span>
                <input
                  value={formData.addressLine2}
                  onChange={(event) => updateField("addressLine2", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">City</span>
                <input
                  value={formData.city}
                  onChange={(event) => updateField("city", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  State or region
                </span>
                <input
                  value={formData.stateRegion}
                  onChange={(event) => updateField("stateRegion", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Postal code
                </span>
                <input
                  value={formData.postalCode}
                  onChange={(event) => updateField("postalCode", event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Household size
                </span>
                <select
                  value={formData.householdSize}
                  onChange={(event) => updateHouseholdSize(event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  required
                >
                  {householdSizeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  I am submitting this as
                </span>
                <select
                  value={formData.membershipInterest}
                  onChange={(event) =>
                    updateField("membershipInterest", event.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                >
                  <option value="member">A new member registration</option>
                  <option value="visitor">A visitor who wants updates</option>
                  <option value="household_update">A household information update</option>
                </select>
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred contact method
                </span>
                <select
                  value={formData.communicationPreference}
                  onChange={(event) =>
                    updateField("communicationPreference", event.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="either">Either email or phone</option>
                </select>
              </label>

              <label className="block sm:col-span-2">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Donation interest
                </span>
                <select
                  value={formData.stewardshipPreference}
                  onChange={(event) =>
                    handleStewardshipPreferenceChange(event.target.value)
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                >
                  <option value="not_specified">Not specified at this time</option>
                  <option value="one_time">Interested in one-time giving</option>
                  <option value="recurring">Interested in recurring giving</option>
                </select>
              </label>
            </div>

            {getAdditionalMemberCount(
              formData.householdSize,
              formData.householdMembers
            ) > 0 && (
              <div className="mt-8 rounded-3xl border border-[#C9A227]/20 bg-[#fff8ec] p-5">
                <div className="mb-5">
                  <h3 className="font-serif-elegant text-xl font-bold text-[#722F37]">
                    Household Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Tell us how many people are in your household. You may
                    optionally add names and details for each household member.
                  </p>
                </div>

                <div className="space-y-5">
                  {normalizeHouseholdMembers(
                    formData.householdSize,
                    formData.householdMembers
                  ).map((member, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-[#722F37]/15 bg-white p-4 shadow-sm"
                    >
                      <p className="mb-4 text-sm font-semibold text-[#722F37]">
                        Household member {index + 2} optional
                      </p>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-[minmax(110px,1fr)_minmax(110px,1fr)_minmax(150px,1.2fr)_minmax(130px,1fr)_minmax(180px,1.5fr)_minmax(130px,1fr)]">
                        <label className="block min-w-0">
                          <span className="mb-2 block whitespace-nowrap text-sm font-semibold tracking-normal text-gray-600">
                            First name
                          </span>
                          <input
                            value={member.firstName || ""}
                            onChange={(event) =>
                              updateHouseholdMember(
                                index,
                                "firstName",
                                event.target.value
                              )
                            }
                            className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-[0.95rem] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                          />
                        </label>
                        <label className="block min-w-0">
                          <span className="mb-2 block whitespace-nowrap text-sm font-semibold tracking-normal text-gray-600">
                            Last name
                          </span>
                          <input
                            value={member.lastName || ""}
                            onChange={(event) =>
                              updateHouseholdMember(
                                index,
                                "lastName",
                                event.target.value
                              )
                            }
                            className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-[0.95rem] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                          />
                        </label>
                        <label className="block min-w-0">
                          <span className="mb-2 block whitespace-nowrap text-sm font-semibold tracking-normal text-gray-600">
                            Relationship
                          </span>
                          <select
                            value={member.relationship || ""}
                            onChange={(event) =>
                              updateHouseholdMember(
                                index,
                                "relationship",
                                event.target.value
                              )
                            }
                            className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-[0.95rem] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                          >
                            {relationshipOptions.map(([value, label]) => (
                              <option key={value || "blank"} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block min-w-0">
                          <span className="mb-2 block whitespace-nowrap text-sm font-semibold tracking-normal text-gray-600">
                            Age group
                          </span>
                          <select
                            value={member.ageGroup || ""}
                            onChange={(event) =>
                              updateHouseholdMember(
                                index,
                                "ageGroup",
                                event.target.value
                              )
                            }
                            className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-[0.95rem] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                          >
                            {ageGroupOptions.map(([value, label]) => (
                              <option key={value || "blank"} value={value}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block min-w-0">
                          <span className="mb-2 block whitespace-nowrap text-sm font-semibold tracking-normal text-gray-600">
                            Email
                          </span>
                          <input
                            type="email"
                            value={member.email || ""}
                            onChange={(event) =>
                              updateHouseholdMember(index, "email", event.target.value)
                            }
                            className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-[0.95rem] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                          />
                        </label>
                        <label className="block min-w-0">
                          <span className="mb-2 block whitespace-nowrap text-sm font-semibold tracking-normal text-gray-600">
                            Phone
                          </span>
                          <input
                            value={member.phone || ""}
                            onChange={(event) =>
                              updateHouseholdMember(index, "phone", event.target.value)
                            }
                            className="h-11 w-full min-w-0 rounded-xl border border-gray-300 bg-white px-3 text-[0.95rem] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                {formData.householdSize === "10+" && (
                  <button
                    type="button"
                    onClick={addHouseholdMember}
                    className="mt-5 rounded-xl border border-[#722F37]/30 px-4 py-2 text-sm font-semibold text-[#722F37] hover:bg-[#722F37]/10"
                  >
                    Add another household member
                  </button>
                )}
              </div>
            )}

            <div className="mt-8 rounded-3xl border border-[#C9A227]/20 bg-[#fff8ec] p-5">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Membership payment option
              </p>
              <div className="grid gap-3">
                {membershipPaymentModeOptions.map((option) => {
                  const active = formData.membershipPaymentMode === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => updateMembershipPaymentMode(option.id)}
                      className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                        active
                          ? "border-[#722F37] bg-[#722F37] text-white"
                          : "border-[#C9A227]/20 bg-white text-gray-800"
                      }`}
                      aria-pressed={active}
                    >
                      <span className="block text-sm font-semibold">
                        {option.title}
                      </span>
                      <span
                        className={`mt-1 block text-xs ${
                          active ? "text-white/80" : "text-gray-500"
                        }`}
                      >
                        {option.description}
                      </span>
                    </button>
                  );
                })}
              </div>
              {formData.membershipPaymentMode === "choose_contribution" && (
                <div className="mt-5 rounded-2xl border border-[#C9A227]/20 bg-white p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Contribution type
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {contributionOptions.map((option) => {
                      const active = formData.contributionType === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => updateField("contributionType", option.id)}
                          className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                            active
                              ? "border-[#722F37] bg-[#722F37] text-white"
                              : "border-[#C9A227]/20 bg-[#fff8ec] text-gray-800"
                          }`}
                          aria-pressed={active}
                        >
                          <span className="block text-sm font-semibold">
                            {option.title}
                          </span>
                          <span
                            className={`mt-1 block text-xs ${
                              active ? "text-white/80" : "text-gray-500"
                            }`}
                          >
                            {option.description}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Paid membership card details are entered only on Stripe-hosted
                    Checkout after email verification. Stripe emails the receipt when
                    payment is confirmed.
                  </p>
                </div>
              )}
            </div>

            {formData.stewardshipPreference !== "not_specified" && (
              <div className="mt-8 rounded-3xl border border-[#C9A227]/20 bg-[#fff8ec] p-5">
                <div className="mb-4 rounded-2xl border border-[#722F37]/15 bg-white px-4 py-3 text-sm text-gray-700">
                  After you submit this form and verify your email, you will be
                  redirected to Stripe-hosted Checkout to enter contact information
                  and card details securely.
                </div>
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Suggested amount
                </p>
                <div className="flex flex-wrap gap-3 mb-4">
                  {stewardshipSuggestedAmounts.map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => updateField("stewardshipAmount", String(value))}
                      className={`px-4 py-2 rounded-full border text-sm font-medium ${
                        Number(formData.stewardshipAmount) === value
                          ? "bg-[#C9A227] text-[#722F37] border-[#C9A227]"
                          : "border-[#C9A227]/30 text-[#722F37] bg-white"
                      }`}
                      aria-pressed={Number(formData.stewardshipAmount) === value}
                    >
                      ${value}
                    </button>
                  ))}
                </div>

                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    Amount you would like to give
                  </span>
                  <input
                    type="number"
                    min="1"
                    max="50000"
                    step="1"
                    value={formData.stewardshipAmount}
                    onChange={(event) =>
                      updateField("stewardshipAmount", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                    placeholder="Enter an intended amount"
                    required
                  />
                </label>

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#722F37] px-5 py-3 font-semibold text-white hover:bg-[#5A252C] disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {status === "loading"
                    ? "Submitting..."
                    : "Submit form and continue after verification"}
                </button>
              </div>
            )}

            <div className="mt-8">
              <p className="text-sm font-medium text-gray-700 mb-3">
                Ways you may want to serve
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {servingOptions.map((option) => (
                  <label
                    key={option}
                    className="flex items-start gap-3 rounded-2xl border border-[#C9A227]/20 bg-[#f7f1e8] px-4 py-3 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={formData.servingInterests.includes(option)}
                      onChange={() => toggleServingInterest(option)}
                      className="mt-1"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>

              {formData.servingInterests.includes("Other") && (
                <label className="block mt-4">
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    Please describe how you would like to serve
                  </span>
                  <input
                    value={formData.otherServingInterest}
                    onChange={(event) =>
                      updateField("otherServingInterest", event.target.value)
                    }
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                    placeholder="Describe your service interest"
                  />
                </label>
              )}
            </div>

            <label className="block mt-8">
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Notes for the parish
              </span>
              <textarea
                rows="5"
                value={formData.notes}
                onChange={(event) => updateField("notes", event.target.value)}
                className="w-full rounded-2xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                placeholder="Share anything the parish should know about your household, sacraments, youth needs, language needs, or preferred involvement."
              />
            </label>

            <label className="flex items-start gap-3 mt-6 rounded-2xl border border-[#C9A227]/20 bg-[#fff8ec] px-4 py-4 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.canReceiveUpdates}
                onChange={(event) => updateField("canReceiveUpdates", event.target.checked)}
                className="mt-1"
              />
              <span>
                I consent to receive parish communications, service updates, and membership
                follow-up from {siteMeta.shortName}.
              </span>
            </label>

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-[#722F37] px-6 py-3 text-white font-medium hover:bg-[#5A252C] disabled:opacity-70"
            >
              {status === "loading" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
              {status === "loading"
                ? "Submitting..."
                : formData.stewardshipPreference !== "not_specified" ||
                  formData.membershipPaymentMode === "choose_contribution"
                ? "Submit and continue after verification"
                : "Submit membership form"}
            </button>
          </motion.form>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="bg-white rounded-3xl p-8 border border-[#C9A227]/10 shadow-sm"
            >
              <h3 className="font-serif-elegant text-xl font-bold text-[#722F37] mb-4">
                What happens after you submit
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li>The parish receives your registration in the secure member system.</li>
                <li>Your form is reviewed by parish leadership.</li>
                <li>The church can contact you for follow-up, household updates, and parish news.</li>
                <li>Submissions are stored in the church database and are not posted publicly.</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14 }}
              className="bg-[#f7f1e8] rounded-3xl p-8 border border-[#C9A227]/20"
            >
              <h3 className="font-serif-elegant text-xl font-bold text-[#722F37] mb-4">
                Good uses for this page
              </h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li>New households joining the parish community</li>
                <li>Existing members updating contact information</li>
                <li>Families asking to receive parish communications</li>
                <li>Members offering to serve in ministry and volunteer roles</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-8 border border-[#C9A227]/10 shadow-sm"
            >
              <h3 className="font-serif-elegant text-xl font-bold text-[#722F37] mb-4">
                Need help instead?
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                If you prefer to register with parish leadership directly, contact the church
                office at <a className="text-[#722F37] underline" href={siteMeta.emailHref}>{siteMeta.email}</a> or call{" "}
                <a className="text-[#722F37] underline" href={siteMeta.phoneHref}>{siteMeta.phone}</a>.
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
